import { getCookie } from 'cookies-next';
import { match } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/function';
import {
  FC,
  ReactNode,
  Reducer,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

import { Currency } from '@catawiki/runtime-toolkit';

import { fetchCurrentUser } from '@/shared/api/user';
import { COOKIES } from '@/shared/constants/cookies';
import { isLoggedIn } from '@/shared/cookies';
import { BiddingCurrency } from '@/shared/models/money';
import { userAllowsMarketingCookies } from '@/shared/utils/getMarketingCookiePermission';

export enum UserDataStatus {
  Unset = 'UserData/Unset',
  Loading = 'UserData/Loading',
  Success = 'UserData/Success',
  Error = 'UserData/Error',
}

export interface UserData {
  isLogged: boolean;
  bidderToken: string;
  currencyCode: BiddingCurrency;
  userName: string;
  country: {
    shortCode: string;
  };
  userAllowsMarketingCookies: boolean;
  userId?: number;
  didAcceptExplicitContent?: boolean;
  currentLocation?: string;
}

export interface UserDataState extends UserData {
  userDataStatus: UserDataStatus;
}

export const initialUserDataState: UserDataState = {
  isLogged: false,
  didAcceptExplicitContent: !!getCookie(COOKIES.EXPLICIT_CONTENT_AGREED),
  userAllowsMarketingCookies,
  userName: '',
  bidderToken: '',
  currencyCode: Currency.Currency.EURO,
  country: {
    shortCode: '',
  },
  userDataStatus: UserDataStatus.Unset,
};

interface UserContext {
  currentUser: UserDataState;
  getCurrentUser?: () => void;
}

export const UserContext = createContext<UserContext>({
  currentUser: initialUserDataState,
});

interface Props {
  children: ReactNode;
}

const userDataLoading = () => ({ type: 'dataLoading' as const });
type UserDataLoading = ReturnType<typeof userDataLoading>;

const userDataFailure = () => ({ type: 'dataFailure' as const });
type UserDataFailure = ReturnType<typeof userDataFailure>;

const userDataSuccessful = (payload: UserData) => ({
  type: 'dataSuccessful' as const,
  payload,
});
type UserDataSuccessful = ReturnType<typeof userDataSuccessful>;

const reducer: Reducer<
  UserDataState,
  UserDataLoading | UserDataFailure | UserDataSuccessful
> = (state, action) => {
  switch (action.type) {
    case 'dataLoading':
      return { ...state, userDataStatus: UserDataStatus.Loading };
    case 'dataSuccessful':
      return {
        ...state,
        ...action.payload,
        userDataStatus: UserDataStatus.Success,
      };
    case 'dataFailure':
      return {
        ...state,
        ...initialUserDataState,
        userDataStatus: UserDataStatus.Error,
      };
    default:
      return state;
  }
};

export const UserProvider: FC<Props> = ({ children }) => {
  const [userData, dispatch] = useReducer(reducer, initialUserDataState);

  const getCurrentUser = useCallback(async () => {
    if (!isLoggedIn()) return;
    dispatch(userDataLoading());

    pipe(
      await fetchCurrentUser(),
      match(
        () => {
          dispatch(userDataFailure());
        },
        ({ user }) => {
          const data: UserData = {
            isLogged: !!user.id,
            bidderToken: user.bidder_token,
            userId: user.id,
            userName: user.user_name,
            currencyCode: user.bidding_currency?.code,
            country: {
              shortCode: user.country.short_code || '',
            },
            userAllowsMarketingCookies,
          };

          dispatch(userDataSuccessful(data));
        }
      )
    );
  }, []);

  const contextValue = useMemo(
    () => ({
      currentUser: userData,
      getCurrentUser,
    }),
    [userData, getCurrentUser]
  );

  useEffect(() => {
    getCurrentUser();
  }, [getCurrentUser]);

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};
